import { useState, useCallback, useEffect, useMemo, useReducer } from 'react';
import cloneDeep from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';

import useGridApi from '../hooks/useGridApi.hook';

import getChangedRows from './getChangedRows';
import { actionCreators } from './editModeActionCreators';
import { initialState, createReducer } from './editModeLogicReducer';
import useAgGridCellEditModeInfo from './useAgGridCellEditModeInfo.hook';
import isSameRow from './isSameRow';
import { rowsHaveSameContent } from './rowsHaveSameContent';

const getLatestRowFromGrid = (row, gridApi, getUniqueRowId) => {
  if (!gridApi || !getUniqueRowId) {
    return row;
  }

  const rowId = getUniqueRowId({ data: row });

  if (rowId == null || rowId === '') {
    return row;
  }

  const gridRow = gridApi.getRowNode?.(rowId)?.data;

  return gridRow ? { ...gridRow, ...row } : row;
};

const collectRowDataChangesFromGrid = ({
  gridApi,
  rowData,
  rowsToAdd,
  modifiedRows,
  rowsToDelete,
  getUniqueRowId,
}) => {
  const deleteRowIds = new Set(
    rowsToDelete.map(row => getUniqueRowId({ data: row })).filter(id => id != null && id !== ''),
  );
  const addRowIds = new Set(
    rowsToAdd.map(row => getUniqueRowId({ data: row })).filter(id => id != null && id !== ''),
  );
  const modifiedRowIds = new Set(
    modifiedRows.map(row => getUniqueRowId({ data: row })).filter(id => id != null && id !== ''),
  );
  const originalRowsById = new Map(
    (rowData || []).map(row => [getUniqueRowId({ data: row }), row]),
  );
  const pendingRowsById = new Map(
    rowsToAdd
      .map(row => [getUniqueRowId({ data: row }), row])
      .filter(([rowId]) => rowId != null && rowId !== ''),
  );
  const modifiedRowsById = new Map(
    modifiedRows
      .map(row => [getUniqueRowId({ data: row }), row])
      .filter(([rowId]) => rowId != null && rowId !== ''),
  );

  const getEffectiveRow = gridRow => {
    if (!gridRow) {
      return gridRow;
    }

    const rowId = getUniqueRowId({ data: gridRow });

    if (rowId != null && pendingRowsById.has(rowId)) {
      return pendingRowsById.get(rowId);
    }

    if (rowId != null && modifiedRowsById.has(rowId)) {
      const originalRow = originalRowsById.get(rowId);

      return originalRow
        ? { ...originalRow, ...modifiedRowsById.get(rowId) }
        : modifiedRowsById.get(rowId);
    }

    return gridRow;
  };

  const syncedRowsToAdd = [];
  const syncedModifiedRows = [];

  gridApi?.forEachNode(({ data }) => {
    if (!data) {
      return;
    }

    const rowId = getUniqueRowId({ data });
    const effectiveRow = getEffectiveRow(data);

    if (rowId == null || rowId === '' || deleteRowIds.has(rowId)) {
      return;
    }

    const originalRow = originalRowsById.get(rowId);

    if (addRowIds.has(rowId)) {
      syncedRowsToAdd.push(effectiveRow);
      return;
    }

    if (originalRow) {
      if (!isEqual(originalRow, effectiveRow)) {
        syncedModifiedRows.push(effectiveRow);
      }
      return;
    }

    if (modifiedRowIds.has(rowId)) {
      syncedModifiedRows.push(effectiveRow);
    }
  });

  rowsToAdd.forEach(row => {
    const rowId = getUniqueRowId({ data: row });

    if (rowId == null || rowId === '' || deleteRowIds.has(rowId)) {
      return;
    }

    if (!syncedRowsToAdd.some(existingRow => isSameRow(existingRow, row, getUniqueRowId))) {
      syncedRowsToAdd.push(row);
    }
  });

  modifiedRows.forEach(row => {
    const rowId = getUniqueRowId({ data: row });

    if (rowId == null || rowId === '' || deleteRowIds.has(rowId) || addRowIds.has(rowId)) {
      return;
    }

    const originalRow = originalRowsById.get(rowId);

    if (!originalRow || isEqual(originalRow, row)) {
      return;
    }

    if (!syncedModifiedRows.some(existingRow => isSameRow(existingRow, row, getUniqueRowId))) {
      syncedModifiedRows.push(row);
    }
  });

  return findRowDataChanges({
    modifiedRows: syncedModifiedRows,
    rowsToAdd: syncedRowsToAdd,
    rowsToDelete,
    rowData,
    getUniqueRowId,
  });
};

const findRowDataChanges = ({ modifiedRows, rowsToAdd, rowsToDelete, rowData, getUniqueRowId }) => {
  const addRowIds = new Set(
    rowsToAdd.map(row => getUniqueRowId({ data: row })).filter(id => id != null && id !== ''),
  );

  const rowsPairsWithChanges = getChangedRows({
    getUniqueRowId,
    modifiedRows: modifiedRows.filter(
      modifiedRow =>
        !rowsToDelete.find(row => isSameRow(modifiedRow, row, getUniqueRowId)) &&
        !addRowIds.has(getUniqueRowId({ data: modifiedRow })),
    ),
    originalRows: rowData,
  });

  return {
    rowsPairsWithChanges,
    rowsToAdd,
    rowsToDelete,
  };
};

const useEditModeLogic = ({
  appendNewRows,
  rowData,
  getUniqueRowId,
  onCancel = () => null,
  onSave = param => param,
  startInEditMode = false,
  appendRowUnderSelectedRow = false,
  turnOffEditModeOnSave = false,
} = {}) => {
  const { gridApi, onGridReady } = useGridApi();
  const [isInEditMode, setIsInEditMode] = useState(startInEditMode);
  const [isSaving, setIsSaving] = useState(false);
  const [gridDataVersion, setGridDataVersion] = useState(0);
  const [state, dispatch] = useReducer(createReducer(getUniqueRowId), initialState);
  const { clonedRowData, modifiedRows, rowsToDelete, rowsToAdd } = state;

  useEffect(() => {
    const clonedRowData = cloneDeep(rowData ?? []);
    dispatch(actionCreators.syncRowData(clonedRowData));
  }, [rowData]);

  const displayRowData = useMemo(() => {
    const deleteRowIds = new Set(
      rowsToDelete
        .map(row => getUniqueRowId({ data: row }))
        .filter(rowId => rowId != null && rowId !== ''),
    );

    let visibleClonedRows = (clonedRowData ?? []).filter(row => {
      const rowId = getUniqueRowId({ data: row });

      return rowId == null || rowId === '' || !deleteRowIds.has(rowId);
    });

    if (modifiedRows.length) {
      const modifiedRowsById = new Map(
        modifiedRows
          .map(row => [getUniqueRowId({ data: row }), row])
          .filter(([rowId]) => rowId != null && rowId !== ''),
      );

      visibleClonedRows = visibleClonedRows.map(row => {
        const rowId = getUniqueRowId({ data: row });

        return modifiedRowsById.get(rowId) ?? row;
      });
    }

    if (!appendNewRows) {
      return visibleClonedRows;
    }

    const existingRowIds = new Set(
      visibleClonedRows
        .map(row => getUniqueRowId({ data: row }))
        .filter(rowId => rowId != null && rowId !== ''),
    );

    const pendingRowsById = new Map(
      rowsToAdd
        .map(row => [getUniqueRowId({ data: row }), row])
        .filter(([rowId]) => rowId != null && rowId !== ''),
    );

    const pendingNewRows = rowsToAdd.filter(row => {
      const rowId = getUniqueRowId({ data: row });

      if (rowId == null || rowId === '' || existingRowIds.has(rowId) || deleteRowIds.has(rowId)) {
        return false;
      }

      return !visibleClonedRows.some(clonedRow =>
        rowsHaveSameContent(row, clonedRow, getUniqueRowId),
      );
    });

    let result = [...visibleClonedRows, ...pendingNewRows];

    if (gridApi?.forEachNode) {
      const gridRowsById = new Map();
      const modifiedRowsById = new Map(
        modifiedRows
          .map(row => [getUniqueRowId({ data: row }), row])
          .filter(([rowId]) => rowId != null && rowId !== ''),
      );

      gridApi.forEachNode(({ data }) => {
        if (!data) {
          return;
        }

        const rowId = getUniqueRowId({ data });

        if (rowId != null && rowId !== '') {
          gridRowsById.set(rowId, data);
        }
      });

      result = result.map(row => {
        const rowId = getUniqueRowId({ data: row });

        if (rowId != null && pendingRowsById.has(rowId)) {
          return pendingRowsById.get(rowId);
        }

        if (rowId != null && modifiedRowsById.has(rowId)) {
          return modifiedRowsById.get(rowId);
        }

        const gridRow = gridRowsById.get(rowId);

        return gridRow ? { ...gridRow, ...row } : row;
      });
    } else if (modifiedRows.length) {
      const modifiedRowsById = new Map(
        modifiedRows
          .map(row => [getUniqueRowId({ data: row }), row])
          .filter(([rowId]) => rowId != null && rowId !== ''),
      );

      result = result.map(row => {
        const rowId = getUniqueRowId({ data: row });

        return modifiedRowsById.get(rowId) ?? row;
      });
    }

    return result;
  }, [
    appendNewRows,
    clonedRowData,
    modifiedRows,
    rowsToAdd,
    rowsToDelete,
    getUniqueRowId,
    gridApi,
    gridDataVersion,
  ]);

  const addRow = useCallback(
    insertedRow => {
      dispatch(actionCreators.addRow(insertedRow, appendNewRows));

      if (appendNewRows) {
        return insertedRow;
      }

      const newRowId = gridApi.getSelectedNodes().length
        ? gridApi.getSelectedNodes()[0].rowIndex + 1
        : 0;

      const { add: addedRows } = gridApi.applyTransaction({
        add: [insertedRow],
        addIndex: appendRowUnderSelectedRow ? newRowId : null,
      });
      return addedRows[0];
    },
    [appendNewRows, appendRowUnderSelectedRow, gridApi],
  );
  // for groupEdit
  const updateGroup = useCallback(
    modifiedGroupRows => {
      dispatch(actionCreators.updateGroup(modifiedGroupRows));
      gridApi.applyTransaction({ update: modifiedGroupRows });
    },
    [gridApi],
  );
  const updateRow = useCallback(
    modifiedRow => {
      const isPendingAdd =
        appendNewRows && rowsToAdd.some(row => isSameRow(row, modifiedRow, getUniqueRowId));

      const latestRow = isPendingAdd
        ? modifiedRow
        : getLatestRowFromGrid(modifiedRow, gridApi, getUniqueRowId);

      dispatch(actionCreators.updateRow(latestRow));

      if (appendNewRows) {
        gridApi?.applyTransaction({ update: [latestRow] });
        setGridDataVersion(version => version + 1);
      } else {
        gridApi?.applyTransaction({ update: [latestRow] });
      }
    },
    [appendNewRows, gridApi, getUniqueRowId, rowsToAdd],
  );
  const deleteRow = useCallback(
    deletedRow => {
      dispatch(actionCreators.deleteRow(deletedRow));

      if (!appendNewRows) {
        gridApi.applyTransaction({ remove: [deletedRow] });
      }
    },
    [appendNewRows, gridApi],
  );

  const updateRowForSelectAll = useCallback(modifiedRow => {
    dispatch(actionCreators.updateRow(modifiedRow));
  }, []);

  const findChanges = useCallback(() => {
    if (appendNewRows && gridApi) {
      return collectRowDataChangesFromGrid({
        gridApi,
        rowData,
        rowsToAdd,
        modifiedRows,
        rowsToDelete,
        getUniqueRowId,
      });
    }

    return findRowDataChanges({ modifiedRows, rowsToAdd, rowsToDelete, rowData, getUniqueRowId });
  }, [appendNewRows, gridApi, rowsToAdd, modifiedRows, rowsToDelete, rowData, getUniqueRowId]);

  const revertChanges = useCallback(() => {
    gridApi?.stopEditing();
    const changesToCancel = findChanges();
    const isAnyChangeToCancel =
      Boolean(changesToCancel.rowsPairsWithChanges.length) ||
      Boolean(changesToCancel.rowsToAdd.length) ||
      Boolean(changesToCancel.rowsToDelete.length);

    if (!isAnyChangeToCancel) {
      return;
    }

    // these rows are not in the clonedRowData, they are only present
    // in the internal agGrid state, so we remove them by hand,
    // just as we added them by hand
    if (!appendNewRows) {
      gridApi?.applyTransaction({
        add: changesToCancel.rowsToDelete,
        remove: changesToCancel.rowsToAdd,
      });
    }

    const clonedRowData = cloneDeep(rowData);
    dispatch(actionCreators.changeRowData(clonedRowData));
  }, [appendNewRows, findChanges, gridApi, rowData]);

  const revertChangesAndCancel = useCallback(() => {
    revertChanges();
    onCancel();
  }, [revertChanges, onCancel]);

  const findChangesAndSave = useCallback(async () => {
    gridApi?.stopEditing(false);

    const changesToSave = collectRowDataChangesFromGrid({
      gridApi,
      rowData,
      rowsToAdd,
      modifiedRows,
      rowsToDelete,
      getUniqueRowId,
    });

    setIsSaving(true);

    try {
      const response = await onSave(changesToSave);

      if (response && response.shouldCancelHookStateReset) {
        return changesToSave;
      }

      if (turnOffEditModeOnSave) {
        setIsInEditMode(false);
      }

      dispatch(actionCreators.resetChanges());
      return changesToSave;
    } finally {
      setIsSaving(false);
    }
  }, [
    appendNewRows,
    gridApi,
    modifiedRows,
    rowsToAdd,
    rowsToDelete,
    rowData,
    getUniqueRowId,
    onSave,
    turnOffEditModeOnSave,
  ]);

  const isInAgGridEditMode = useAgGridCellEditModeInfo(gridApi);

  const getAllRows = useCallback(
    (isFetchAllRows = false) => {
      const rows = [];
      const forEachMethod = isFetchAllRows ? 'forEachNode' : 'forEachNodeAfterFilterAndSort';
      gridApi?.[forEachMethod](({ data }) => rows.push(data));
      return rows;
    },
    [gridApi],
  );

  const isDirty = useMemo(() => {
    if (isSaving) {
      return false;
    }

    if (rowsToDelete.length > 0 || rowsToAdd.length > 0) {
      return true;
    }

    return (
      getChangedRows({
        getUniqueRowId,
        modifiedRows,
        originalRows: rowData,
      }).length > 0
    );
  }, [isSaving, modifiedRows, rowsToAdd, rowsToDelete, rowData, getUniqueRowId, gridDataVersion]);

  return {
    gridApi,
    clonedRowData,
    displayRowData,
    onGridReady,
    addRow,
    updateGroup,
    updateRow,
    deleteRow,
    updateRowForSelectAll,
    getAllRows,

    isInEditMode,
    setIsInEditMode,
    isInAgGridEditMode,
    isDirty,

    findChanges,
    findChangesAndSave,
    revertChanges,
    revertChangesAndCancel,
  };
};

export default useEditModeLogic;
